import React, { useState } from 'react'
import { useAppSelector, useAppDispatch } from '@/store/hooks'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from '@/hooks/useSnackbar'
import { useCurrency } from '@/contexts/CurrencyContext'
import {
    clearPaymentReference,
} from '@/store/slices/subscriptionSlice'
import { SubscriptionPlanCard } from '@/components/subscription/SubscriptionPlanCard'
import { SubscriptionStatusBadge } from '@/components/subscription/SubscriptionStatusBadge'
import { PaymentModal } from '@/components/subscription/PaymentModal'
import { MonthlyLimitsDisplay } from '@/components/subscription/MonthlyLimitsDisplay'
import { CommissionBreakdown } from '@/components/subscription/CommissionBreakdown'
import {
    useGetPlansQuery,
    useGetCurrentSubscriptionQuery,
    useInitializePaymentMutation,
} from '@/services/subscriptionRtkApi'

export const SubscriptionPage: React.FC = () => {
    const navigate = useNavigate()
    const { showSuccess, showError } = useSnackbar()
    const dispatch = useAppDispatch()
    const { user } = useAppSelector((state) => state.auth)
    const { userCurrency } = useCurrency()
    const { paymentAuthorizationUrl } = useAppSelector((state) => state.subscription)

    const { data: plans = [], isLoading: plansLoading } = useGetPlansQuery()
    const { data: currentSubscription, isLoading: subscriptionLoading } = useGetCurrentSubscriptionQuery()
    const [initializePayment, { isLoading: isProcessingPayment }] = useInitializePaymentMutation()

    const [showPaymentModal, setShowPaymentModal] = useState(false)
    const [selectedTier, setSelectedTier] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)

    const handleSelectPlan = async (tier: string) => {
        if (!user) {
            navigate('/login')
            return
        }

        if (tier === 'free') {
            showSuccess('Free tier selected. Your subscription will be updated.')
            return
        }

        setSelectedTier(tier)
        setShowPaymentModal(true)
        setError(null)

        try {
            const supportedCurrencies: ('GHS' | 'USD')[] = ['GHS', 'USD']
            let paymentCurrency: 'GHS' | 'USD' = 'GHS'
            
            if (userCurrency && supportedCurrencies.includes(userCurrency as any)) {
                paymentCurrency = userCurrency as 'GHS' | 'USD'
            }

            await initializePayment({
                tier: tier as 'free' | 'pro' | 'enterprise' | 'business',
                currency: paymentCurrency,
                redirectUrl: `${window.location.origin}/subscription/callback`,
            }).unwrap()
        } catch (err: any) {
            setError(err?.message || 'Failed to initialize payment')
            showError(err?.message || 'Failed to initialize payment')
            setShowPaymentModal(false)
        }
    }

    // Get current tier for comparison
    const currentTier = currentSubscription?.tier || 'free'

    return (
        <div className="relative py-1 sm:py-2">
            <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-72 bg-gradient-to-b from-cyan-100/60 to-transparent dark:from-cyan-900/20" />

            <div className="mb-3 sm:mb-4">
                <button
                    onClick={() => navigate('/dashboard')}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                >
                    ← Back to Dashboard
                </button>
            </div>

            <div className="mb-4 overflow-hidden rounded-3xl border border-slate-200 bg-white/90 p-4 shadow-sm backdrop-blur dark:border-gray-800 dark:bg-gray-900/85 sm:mb-8 sm:p-6">
                <div className="mb-3 flex items-center justify-between gap-2 sm:mb-4">
                    <span className="inline-flex items-center whitespace-nowrap rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
                        Subscription Hub
                    </span>
                    <span className="inline-flex items-center whitespace-nowrap rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs font-semibold text-cyan-700 dark:border-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300">
                        Plan Control
                    </span>
                </div>

                <h1 className="text-lg font-bold text-slate-900 dark:text-white sm:text-xl">
                    Upgrade Your Plan
                </h1>
                <p className="mt-1 text-xs text-slate-600 dark:text-gray-400 sm:text-sm">
                    Choose the perfect plan for your needs
                </p>
                {userCurrency && userCurrency !== 'GHS' && (
                    <p className="mt-1 text-xs text-slate-500 dark:text-gray-400 sm:text-sm">
                        Prices displayed in {userCurrency} based on your currency preference
                    </p>
                )}

                {currentSubscription && (
                    <div className="mt-4 flex">
                        <SubscriptionStatusBadge subscription={currentSubscription} />
                    </div>
                )}
            </div>

            <div className="max-w-7xl mx-auto">

                {/* Current Limits Display */}
                {user && (
                    <div className="mb-6 sm:mb-8">
                        <h2 className="mb-3 text-sm font-semibold text-slate-900 dark:text-white sm:text-base">
                            Your Monthly Limits
                        </h2>
                        <MonthlyLimitsDisplay user={user} />
                    </div>
                )}

                {/* Plans Grid */}
                <div className="mb-8 grid grid-cols-1 gap-3 sm:gap-5 md:grid-cols-2 lg:grid-cols-4">
                    {plansLoading || subscriptionLoading ? (
                        <div className="col-span-full flex justify-center py-12">
                            <div className="animate-spin">
                                <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                            </div>
                        </div>
                    ) : plans && plans.length > 0 ? (
                        plans.map((plan) => (
                            <SubscriptionPlanCard
                                key={plan.tier}
                                plan={plan}
                                isCurrentTier={plan.tier === currentTier}
                                onSelectPlan={handleSelectPlan}
                                loading={isProcessingPayment && selectedTier === plan.tier}
                                userRole={user?.role as 'worker' | 'employer'}
                            />
                        ))
                    ) : (
                        <div className="col-span-full text-center py-12">
                            <p className="text-gray-600 dark:text-gray-400">
                                Unable to load subscription plans. Please try again later.
                            </p>
                        </div>
                    )}
                </div>

                {/* Features Comparison */}
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
                    <div className="px-3 py-4 sm:px-6 sm:py-6">
                        <h2 className="mb-3 text-sm font-bold text-slate-900 dark:text-white sm:text-base">
                            Detailed Comparison
                        </h2>

                        <div className="overflow-x-auto">
                            <table className="w-full text-xs sm:text-sm">
                                <thead>
                                    <tr className="border-b-2 border-gray-200 dark:border-gray-700">
                                        <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">
                                            Feature
                                        </th>
                                        {plans && plans.map((plan) => (
                                            <th key={plan.tier} className="px-4 py-3 text-center font-semibold text-gray-900 dark:text-white">
                                                {plan.name}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* Monthly Limits - Role-specific */}
                                    {user?.role === 'employer' ? (
                                        <tr className="border-b border-gray-200 dark:border-gray-700">
                                            <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                                                Gigs per Month
                                            </td>
                                            {plans && plans.map((plan) => (
                                                <td key={plan.tier} className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">
                                                    {plan.limits?.gigs === 9999 ? '∞' : plan.limits?.gigs}
                                                </td>
                                            ))}
                                        </tr>
                                    ) : (
                                        <tr className="border-b border-gray-200 dark:border-gray-700">
                                            <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                                                Applications per Month
                                            </td>
                                            {plans && plans.map((plan) => (
                                                <td key={plan.tier} className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">
                                                    {plan.limits?.applications === 9999 ? '∞' : plan.limits?.applications}
                                                </td>
                                            ))}
                                        </tr>
                                    )}

                                    {/* Commission Rates */}
                                    <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                                            Platform Commission
                                        </td>
                                        {plans && plans.map((plan) => (
                                            <td key={plan.tier} className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">
                                                {(plan.platformCommission * 100).toFixed(0)}%
                                            </td>
                                        ))}
                                    </tr>
                                    <tr className="border-b border-gray-200 dark:border-gray-700">
                                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                                            Buyer Protection Fee
                                        </td>
                                        {plans && plans.map((plan) => (
                                            <td key={plan.tier} className="px-4 py-3 text-center text-gray-600 dark:text-gray-400">
                                                {(plan.buyerProtectionFee * 100).toFixed(0)}%
                                            </td>
                                        ))}
                                    </tr>

                                    {/* Features */}
                                    <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                                            Advanced Analytics
                                        </td>
                                        {plans && plans.map((plan) => (
                                            <td key={plan.tier} className="px-4 py-3 text-center">
                                                {plan.features.advancedAnalytics ? '✓' : '✗'}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr className="border-b border-gray-200 dark:border-gray-700">
                                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                                            Priority Support
                                        </td>
                                        {plans && plans.map((plan) => (
                                            <td key={plan.tier} className="px-4 py-3 text-center">
                                                {plan.features.prioritySupport ? '✓' : '✗'}
                                            </td>
                                        ))}
                                    </tr>
                                    <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                                            API Access
                                        </td>
                                        {plans && plans.map((plan) => (
                                            <td key={plan.tier} className="px-4 py-3 text-center">
                                                {plan.features.apiAccess ? '✓' : '✗'}
                                            </td>
                                        ))}
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Commission Comparison Section */}
                <div className="mt-8 grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2">
                    <div>
                        <h2 className="mb-3 text-sm font-bold text-slate-900 dark:text-white sm:text-base">
                            Commission Breakdown by Tier
                        </h2>
                        <div className="space-y-3 sm:space-y-4">
                            {plans && plans.map((plan) => (
                                <div key={plan.tier} className="rounded-xl border border-slate-200 bg-white p-3 dark:border-gray-800 dark:bg-gray-900 sm:p-4">
                                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">{plan.name}</h3>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600 dark:text-gray-400">Platform Fee:</span>
                                            <span className="font-medium text-gray-900 dark:text-white">{(plan.platformCommission * 100).toFixed(0)}%</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600 dark:text-gray-400">Buyer Protection:</span>
                                            <span className="font-medium text-gray-900 dark:text-white">{(plan.buyerProtectionFee * 100).toFixed(0)}%</span>
                                        </div>
                                        <div className="pt-2 border-t border-gray-200 dark:border-gray-700 flex justify-between">
                                            <span className="text-gray-900 dark:text-white font-semibold">Total Deduction:</span>
                                            <span className="font-bold text-red-600 dark:text-red-400">{((plan.platformCommission + plan.buyerProtectionFee) * 100).toFixed(0)}%</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div>
                        <h2 className="mb-3 text-sm font-bold text-slate-900 dark:text-white sm:text-base">
                            Example Earnings
                        </h2>
                        <CommissionBreakdown
                            grossAmount={1000}
                            platformCommission={0.14}
                            buyerProtectionFee={0.02}
                            currency={userCurrency || 'GHS'}
                            compact={false}
                            showDetails={true}
                        />
                    </div>
                </div>

                {/* FAQ Button */}
                <div className="mt-8 flex justify-center">
                    <button
                        onClick={() => navigate('/faq')}
                        className="rounded-xl bg-primary-600 px-5 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-primary-700 sm:px-8 sm:py-3"
                    >
                        View Frequently Asked Questions
                    </button>
                </div>
            </div>

            {/* Payment Modal */}
            <PaymentModal
                isOpen={showPaymentModal}
                onClose={() => {
                    setShowPaymentModal(false)
                    setSelectedTier(null)
                    dispatch(clearPaymentReference())
                }}
                authorizationUrl={paymentAuthorizationUrl || undefined}
                isLoading={isProcessingPayment}
                error={error}
            />
        </div>
    )
}